import { Loader, Modal, Table, TableColumn, TableRow, Tooltip, icons, useToast } from '@lifebit-ai/lds'
import { useQueryClient } from '@tanstack/react-query'
import { DownloadFileMethods } from 'enums'
import React, { useState } from 'react'

import DownloadResultFile from 'modules/resultsV2/DownloadResultFile'
import {
  CopyUrlIcon,
  StyledAction,
  StyledNameContents,
  StyledResultName,
  StyledResultOwner,
  ViewDetailsIcon
} from 'modules/resultsV2/Results.styles'
import FileActionsSkeleton from 'modules/resultsV2/components/FileActionsSkeleton/FileActionsSkeleton'
import FileContent from 'modules/resultsV2/components/FileContent/FileContent'
import { PreviewModalContent } from 'modules/resultsV2/components/FileContent/FileContents.styles'
import { QueryKeys } from 'services/queryKeys'
import { formatDate } from 'utils/date'
import { copyTextToClipboard } from 'utils/string'

import {
  COPY_S3_LINK_ICON_TEST_ID,
  type CloudStorageUriLocation,
  VIEW_DETAILS_ICON_TEST_ID,
  formatCloudStorageUri,
  getCopyCloudStorageLinkTooltip,
  isCompressedFileName
} from './utils'

const byteConverter = (byte: number): string => {
  return `${byte / 1000}kb`
}

interface DirRowProps {
  dir: S3DirAsContent
}

interface FileRowProps {
  file: S3FileAsContent
}

const DirRow: React.FC<DirRowProps> = ({ dir }) => {
  return (
    <StyledNameContents isDir>
      <icons.Folder data-testid="dir-icon" />
      {dir.name}
    </StyledNameContents>
  )
}

const FileRow: React.FC<FileRowProps> = ({ file }) => {
  return (
    <StyledNameContents>
      <icons.File data-testid="file-icon" />
      {file.name}
    </StyledNameContents>
  )
}

interface ResultsListProps {
  resultId: string
  contents: (S3FileAsContent | S3DirAsContent)[]
  bucket: string
  path: string
  provider: string
  storageLocation: CloudStorageUriLocation
  openDir(dirName: string): Promise<void>
  isFile: boolean
  isReadyForDownload: boolean
  isAnalysis: boolean
  isApprovalRestricted: boolean
  isFileViewDisabled?: boolean
  isFileActionsLoading?: boolean
  isLoading?: boolean
  owner?: UserInfo
}

const ResultsList: React.FC<ResultsListProps> = ({
  resultId,
  contents,
  bucket,
  path,
  storageLocation,
  openDir,
  isFile,
  isReadyForDownload,
  isAnalysis,
  owner,
  isApprovalRestricted,
  isFileViewDisabled = false,
  isFileActionsLoading = false,
  isLoading
}) => {
  const [selectedFileToBePreviewed, setSelectedFileToBePreviewed] = useState<S3FileAsContent | undefined>()
  const queryClient = useQueryClient()
  const onViewFile = (item: S3FileAsContent) => setSelectedFileToBePreviewed(item)
  const toast = useToast()
  const copyLinkTooltip = getCopyCloudStorageLinkTooltip(storageLocation)

  const onCopyS3Link = (item: S3FileAsContent): void => {
    const fileKey = isFile ? path : `${path}/${item?.name}`
    copyTextToClipboard(formatCloudStorageUri(storageLocation, fileKey))
    toast({ message: 'File link copied to clipboard' })
  }

  const getRowOverlay = (row: TableRow<S3FileAsContent | S3DirAsContent>) => {
    if (isFileActionsLoading) {
      if (row.original.isDir) return null

      return (
        <StyledAction>
          <FileActionsSkeleton />
        </StyledAction>
      )
    }

    if (isApprovalRestricted || isFileViewDisabled) return null

    return (
      <StyledAction>
        {!row.original.isDir && !isCompressedFileName(row.original.name) && (
          <Tooltip title="Preview">
            <ViewDetailsIcon
              onClick={() => onViewFile(row.original as S3FileAsContent)}
              data-testid={VIEW_DETAILS_ICON_TEST_ID}
            />
          </Tooltip>
        )}
        {!row.original.isDir && (
          <Tooltip title={copyLinkTooltip}>
            <CopyUrlIcon
              onClick={() => onCopyS3Link(row.original as S3FileAsContent)}
              data-testid={COPY_S3_LINK_ICON_TEST_ID}
            />
          </Tooltip>
        )}
        {!row.original.isDir && isReadyForDownload ? (
          <DownloadResultFile
            bucket={bucket as string}
            resultId={resultId as string}
            fileKey={`${isFile ? path : `${path}/${row.original.name}`}`}
            fileName={row.original.name}
            method={DownloadFileMethods.DownloadDirectlyWithHiddenLink}
          />
        ) : null}
      </StyledAction>
    )
  }

  const getColumns = (): TableColumn<S3FileAsContent | S3DirAsContent>[] => {
    return [
      {
        id: 'name',
        title: 'Name',
        render: (row: TableRow<S3FileAsContent | S3DirAsContent>) => {
          return (
            <StyledResultName>
              {row.original.isDir ? (
                <DirRow dir={row.original as S3DirAsContent} />
              ) : (
                <FileRow file={row.original as S3FileAsContent} />
              )}
            </StyledResultName>
          )
        }
      },
      {
        id: 'size',
        title: 'Size',
        width: 200,
        render: (row: TableRow<S3FileAsContent | S3DirAsContent>) => {
          return row.original.isDir ? '..' : byteConverter(row.original.size)
        }
      },
      {
        id: 'lastModified',
        title: 'Last Modified',
        width: 200,
        render: (row: TableRow<S3FileAsContent | S3DirAsContent>) => {
          return row.original.isDir ? 'Unknown' : formatDate(row.original.lastModified)
        }
      },
      {
        id: 'owner',
        title: 'Owner',
        width: 200,
        render: (_row: TableRow<S3FileAsContent | S3DirAsContent>) => {
          return isAnalysis && isFile && <StyledResultOwner>Owned by {owner?.email || 'Unknown'}</StyledResultOwner>
        }
      }
    ]
  }

  const handleRowClick = async (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    row: TableRow<S3FileAsContent | S3DirAsContent>
  ) => {
    if (e.detail === 2 && row.original.isDir) {
      await openDir(row.original.name)
    }
  }

  return (
    <>
      <Table<S3FileAsContent | S3DirAsContent>
        columns={getColumns()}
        data={contents ?? []}
        loading={isLoading ?? false}
        loadingIcon={<Loader text={false} />}
        rowHoverOverlay={getRowOverlay}
        rowKey="path"
        onRowClicked={handleRowClick}
      >
        <Table.TableContainer>
          <Table.Body />
        </Table.TableContainer>
      </Table>

      <Modal
        onCancel={() => {
          queryClient.cancelQueries({ queryKey: QueryKeys.GetFileContentsFromSignedUrl() })
          setSelectedFileToBePreviewed(undefined)
        }}
        open={!!selectedFileToBePreviewed && !isApprovalRestricted && !isFileViewDisabled}
        title={`View details: ${selectedFileToBePreviewed?.name}`}
        size="large"
        closable
      >
        {bucket && path && selectedFileToBePreviewed && (
          <PreviewModalContent>
            <FileContent
              file={selectedFileToBePreviewed}
              resultId={resultId as string}
              bucket={bucket}
              fileKey={isFile ? path : `${path}/${selectedFileToBePreviewed?.name}`}
            />
          </PreviewModalContent>
        )}
      </Modal>
    </>
  )
}

export default ResultsList
